import { useMutation } from "@tanstack/react-query";
import { DOCUMENT_KINDS, type DocumentKind } from "@steelbill/types";
import { api } from "@/lib/api";
import {
  endpoints,
  type ChallanListResponse,
  type CreditNoteListResponse,
  type InvoiceListResponse,
  type QuotationListResponse,
} from "@/lib/endpoints";

/**
 * A scanned number → the document it belongs to (§9 amendment).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT ASKS EVERY REGISTER, because a scanned string does not say what it is. The
 * number formats are distinguishable by eye — an invoice is `26-27/69` (FY
 * first, §6), a quotation is `542/26` (sequence first), a challan is
 * `CHL/26-27/7` — but guessing from the shape would fail on a partial scan, on a
 * hand-typed fragment, and on the day someone records a manual invoice with an
 * unusual number (§6.1 allows it).
 *
 * Asking all of them is three cheap indexed reads and cannot be wrong about
 * which register a document is in.
 *
 * CHALLANS WERE MISSING UNTIL 2026-08-16, AND THAT WAS THE WORST OF THE THREE.
 * This app PRINTS a Code 128 barcode of the challan number onto the challan
 * itself (`services/pdf/challan-pdf.ts`, `modern-challan-pdf.ts`, and
 * `services/pdf/barcode.ts` names `CH-900` as a supported payload) — so pointing
 * the handheld at a sheet SteelBill produced answered "Nothing found for
 * CHL/26-27/7". The scanner is mounted in the sidebar and reachable from every
 * screen. The register was already searchable: `GET /challans?q=` matches the
 * number and the vehicle, and the global search palette has used it since §17.7
 * shipped. Only this resolver's two-armed union had not been widened.
 *
 * THE REGISTERS ARE A `Record<DocumentKind, …>`, NOT A HAND-KEPT LIST. That is
 * the whole repair: a list of "the registers" is a list that silently stops
 * being all of them. Adding §17.3's credit note to `DOCUMENT_KINDS` fails the
 * build here, in the place where the decision (which endpoint, which envelope
 * key) has to be made.
 *
 * AN EXACT MATCH WINS OVER A PARTIAL ONE. Every register searches with
 * `contains`, so "542" matches "542/26" and "1542/26" alike. Opening a document
 * because its number merely CONTAINS what was scanned would be the wrong
 * document, silently — so an exact match is preferred, and an ambiguous partial
 * resolves to nothing rather than to a guess.
 *
 * THE `challans` FLAG IS HANDLED WITH NOTHING, exactly as `use-global-search.ts`
 * handles it. Every challan route is behind `requireFlag("challans")`, which
 * runs before `requireCap` and answers 404, not 403 (§18.3) — so with the module
 * off that one request rejects and its own `.catch(() => null)` swallows it. The
 * scan simply finds nothing in a register that is not switched on. There is
 * deliberately no `useFlag` on the client: `GET /flags` needs `settings.write`,
 * which the OWNER does not hold, so asking would 403 for the one person using
 * the app.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** A row as any register returns it — a number and an id is all a scan needs. */
interface ScannedRow {
  id: number;
  number: string;
}

/**
 * One searchable register: where to ask, and how to get the rows out of the
 * envelope it answers with.
 *
 * THE ENVELOPE KEYS GENUINELY DIFFER — `{ invoices }` for bills, `{ data }` for
 * quotations and challans — which is precisely the kind of per-register detail
 * that gets forgotten when a fourth register is bolted on beside a `Promise.all`
 * of two.
 */
interface DocumentRegister {
  path: string;
  rows: (payload: unknown) => readonly ScannedRow[];
}

/** `{ invoices: [...] }` / `{ data: [...] }` → the rows, or none. */
function envelope<T>(key: keyof T & string) {
  return (payload: unknown): readonly ScannedRow[] => {
    const rows = (payload as Record<string, unknown> | null)?.[key];
    return Array.isArray(rows) ? (rows as ScannedRow[]) : [];
  };
}

export const DOCUMENT_REGISTERS: Record<DocumentKind, DocumentRegister> = {
  invoice: { path: endpoints.invoices, rows: envelope<InvoiceListResponse>("invoices") },
  quotation: { path: endpoints.quotations, rows: envelope<QuotationListResponse>("data") },
  challan: { path: endpoints.challans, rows: envelope<ChallanListResponse>("data") },
  /*
   * §17.3 — and this register is worth scanning for a reason the other three
   * are not. The credit-note sheet PRINTS a Code 128 barcode of `CRN/26-27/1`
   * (`services/pdf/credit-note-pdf.ts` reuses `filingBarcodeBand`), so the
   * handheld will be pointed at one the day the module is switched on. A
   * resolver that did not know the register would answer "Nothing found" about a
   * page this app produced — which is exactly what it did to challans until
   * 2026-08-16.
   *
   * `?q=` here matches the note's OWN number and the frozen
   * `original_invoice_number`, so scanning a BILL also surfaces the notes raised
   * against it. That is a partial match, never an exact one — an invoice number
   * cannot equal a credit note number — so the "exact wins" rule below still
   * opens the bill itself, and the note only wins when nothing else matched at
   * all.
   */
  creditNote: { path: endpoints.creditNotes, rows: envelope<CreditNoteListResponse>("data") },
};

/**
 * What was scanned, resolved.
 *
 * `kind` is the SHARED union, so `ScanAction` routes the hit through
 * `DOCUMENT_KIND_HREF` rather than an `if` per kind — which is what left the
 * challan arm off this in the first place.
 */
export type FoundDocument =
  | { kind: DocumentKind; id: number; number: string }
  | { kind: "none" };

/** Every register's hits for `q`, in `DOCUMENT_KINDS` order, newest concern first. */
async function searchRegisters(q: string): Promise<{ kind: DocumentKind; row: ScannedRow }[]> {
  const search = new URLSearchParams({ q, limit: "5" });

  /*
   * All at once. Sequentially would multiply the wait, and the owner is standing
   * at a desk with a scanner in his hand expecting the document to appear.
   *
   * `catch` PER REGISTER rather than one shared one: if the quotation endpoint
   * is unavailable — or the challan module is switched off, which is a 404 —
   * scanning a bill must still work.
   */
  const results = await Promise.all(
    DOCUMENT_KINDS.map(async (kind) => {
      const register = DOCUMENT_REGISTERS[kind];
      const payload = await api
        .get<unknown>(`${register.path}?${search.toString()}`)
        .catch(() => null);
      return { kind, rows: register.rows(payload) };
    }),
  );

  return results.flatMap(({ kind, rows }) => rows.map((row) => ({ kind, row })));
}

export function useFindDocument() {
  return useMutation<FoundDocument, unknown, string>({
    mutationFn: async (scanned: string) => {
      const q = scanned.trim();
      if (q === "") return { kind: "none" };

      const hits = await searchRegisters(q);

      // Exact first, across every register.
      const exact = hits.find((hit) => hit.row.number === q);
      if (exact !== undefined) {
        return { kind: exact.kind, id: exact.row.id, number: exact.row.number };
      }

      /*
       * No exact hit. A SINGLE partial match across all registers is
       * unambiguous and worth opening — someone typed "27/69" off a creased
       * page. Two or more is a guess, and a guess that opens the wrong bill is
       * worse than saying nothing was found.
       */
      const only = hits.length === 1 ? hits[0] : undefined;
      return only === undefined
        ? { kind: "none" }
        : { kind: only.kind, id: only.row.id, number: only.row.number };
    },
  });
}
